import React, { useState, useEffect } from "react";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import { useToast } from "@components/Toast/ToastContext";
import Modal from "react-modal";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  MdClose,
  MdCheckBoxOutlineBlank,
  MdCheckBox,
  MdSearch,
} from "react-icons/md";

const Cin7ImportModal = ({
  isOpen,
  onClose,
  brandSku,
  brandId,
  protectedFetch,
  onImportSuccess,
}) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState({});
  const [importing, setImporting] = useState(false);
  const [searchSku, setSearchSku] = useState("");
  const [existingSkus, setExistingSkus] = useState(new Set());

  // Fetch existing brand devices to know which SKUs already exist
  useEffect(() => {
    if (!isOpen || !brandId) return;
    protectedFetch(
      apiEndpoints.device.listByBrand(brandId),
      "GET",
      null,
      errorFunctions.checkResponseThenJson,
    )
      .then((res) => {
        const devices = Array.isArray(res) ? res : [];
        setExistingSkus(new Set(devices.map((d) => d.sku)));
      })
      .catch(() => {
        // non-critical
      });
  }, [isOpen, brandId]);

  const fetchCin7Products = (sku) => {
    if (!sku) return;
    setLoading(true);
    protectedFetch(
      apiEndpoints.device.listDeviceCin7(sku),
      "GET",
      null,
      errorFunctions.checkResponseThenJson,
    )
      .then((res) => {
        const data = Array.isArray(res) ? res : res?.results || res?.data || [];
        setResults(Array.isArray(data) ? data : []);
        setSelected({});
      })
      .catch(() => {
        showToast("Failed to fetch CIN7 products", "error");
        setResults([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Auto-fetch on open using brandSku
  useEffect(() => {
    if (!isOpen || !brandSku) return;
    setSearchSku(brandSku);
    fetchCin7Products(brandSku);
  }, [isOpen, brandSku]);

  const handleSearch = () => {
    if (!searchSku.trim()) return;
    fetchCin7Products(searchSku.trim());
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const newResults = results.filter((item) => !existingSkus.has(item.sku));

  const toggleSelectAll = () => {
    if (newResults.length === 0) return;
    const newIndices = results
      .map((item, i) => (!existingSkus.has(item.sku) ? i : -1))
      .filter((i) => i !== -1);
    const allNewSelected = newIndices.every((i) => selected[i]);
    if (allNewSelected) {
      setSelected({});
    } else {
      const newSelected = {};
      newIndices.forEach((i) => {
        newSelected[i] = true;
      });
      setSelected(newSelected);
    }
  };

  const toggleSelectItem = (index) => {
    setSelected((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const newItems = results.filter(
    (_, i) => selected[i] && !existingSkus.has(results[i].sku),
  );
  const existingSelectedItems = results.filter(
    (_, i) => selected[i] && existingSkus.has(results[i].sku),
  );
  const newCount = newItems.length;
  const skipCount = existingSelectedItems.length;

  const handleImport = () => {
    if (selectedCount === 0 || importing) return;

    if (newCount === 0 && skipCount > 0) {
      showToast("Selected SKUs already exist in this brand.", "info");
      return;
    }

    setImporting(true);

    const payload = newItems.map((item) => ({
      brand_id: parseInt(brandId, 10) || 0,
      name: item.name || "",
      weight: item.weight ?? 0,
      height: item.height ?? 0,
      width: item.width ?? 0,
      length: item.length ?? 0,
      description: item.description || "",
      sku: item.sku || "",
      shipping_vendor: "auspost",
    }));

    protectedFetch(apiEndpoints.device.bulk(), "POST", payload, errorFunctions.checkResponseThenJson)
      .then(() => {
        const msg =
          skipCount > 0
            ? `${newCount} device(s) imported, ${skipCount} SKU(s) already existed and were skipped`
            : `${newCount} device(s) imported successfully`;
        showToast(msg, "success");
        onClose();
        onImportSuccess();
        setImporting(false);
        console.log("Import successful:", msg);
      })
      .catch(() => {
        showToast("Import failed. Please try again.", "error");
        setImporting(false);
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={"card"}
      style={{
        overlay: { zIndex: 1050, background: "rgba(0,0,0,0.5)" },
        content: {
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 700,
          maxHeight: "85vh",
          margin: 0,
          padding: 0,
          border: "none",
          borderRadius: 8,
          overflow: "hidden",
        },
      }}
    >
      <div
        className="card-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontWeight: 700, fontSize: "1rem" }}>
          Import dari CIN7
        </span>
        <button onClick={onClose} className={"info-modal-close"}>
          <MdClose />
        </button>
      </div>

      <div className="card-body">
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <div style={{ flex: 1, display: "flex", gap: 8 }}>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search CIN7 by SKU..."
              value={searchSku}
              onChange={(e) => setSearchSku(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              style={{ borderRadius: 8, fontSize: "0.85rem" }}
              disabled={brandSku?.length > 0}
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={handleSearch}
              disabled={loading || !searchSku.trim() || brandSku?.length > 0}
              style={{
                borderRadius: 8,
                fontWeight: 500,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                whiteSpace: "nowrap",
              }}
            >
              <MdSearch size={16} />
              Search
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 32 }}>
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : results.length > 0 ? (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={toggleSelectAll}
                disabled={newResults.length === 0}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  borderRadius: 6,
                  fontSize: "0.8rem",
                  padding: "4px 12px",
                }}
              >
                {newResults.length > 0 &&
                newResults.every((item) => {
                  const idx = results.indexOf(item);
                  return selected[idx];
                }) ? (
                  <MdCheckBox size={18} />
                ) : (
                  <MdCheckBoxOutlineBlank size={18} />
                )}
                {newResults.length > 0 &&
                newResults.every((item) => {
                  const idx = results.indexOf(item);
                  return selected[idx];
                })
                  ? "Deselect All"
                  : "Select All"}
              </button>
              {selectedCount > 0 && (
                <span
                  style={{
                    fontSize: "0.8rem",
                    color: "#6b7280",
                    fontWeight: 500,
                  }}
                >
                  {selectedCount} selected
                </span>
              )}
            </div>

            <TableContainer component={Paper} style={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow style={{ background: "#f9fafb" }}>
                    <TableCell style={{ fontWeight: 700, width: 50 }}>
                      #
                    </TableCell>
                    <TableCell style={{ fontWeight: 700 }}>SKU</TableCell>
                    <TableCell style={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell style={{ fontWeight: 700 }}>Category</TableCell>
                    <TableCell style={{ fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((item, idx) => {
                    const isExisting = existingSkus.has(item.sku);
                    return (
                      <TableRow
                        key={idx}
                        hover={!isExisting}
                        onClick={() => !isExisting && toggleSelectItem(idx)}
                        style={{
                          cursor: isExisting ? "default" : "pointer",
                          background: selected[idx]
                            ? "#eef2ff"
                            : isExisting
                              ? "#f9fafb"
                              : undefined,
                          opacity: isExisting ? 0.6 : 1,
                        }}
                      >
                        <TableCell>
                          {isExisting ? (
                            <MdCheckBoxOutlineBlank size={18} color="#d1d5db" />
                          ) : selected[idx] ? (
                            <MdCheckBox size={18} color="#4f46e5" />
                          ) : (
                            <MdCheckBoxOutlineBlank size={18} color="#9ca3af" />
                          )}
                        </TableCell>
                        <TableCell
                          style={{
                            fontFamily: "monospace",
                            fontSize: "0.85rem",
                          }}
                        >
                          {item.sku || "—"}
                        </TableCell>
                        <TableCell>{item.name || "—"}</TableCell>
                        <TableCell>
                          {item.category_name || item.category || "—"}
                        </TableCell>
                        <TableCell>
                          {isExisting ? (
                            <span
                              style={{
                                fontSize: "0.75rem",
                                color: "#6b7280",
                                fontWeight: 500,
                              }}
                            >
                              Already in brand
                            </span>
                          ) : (
                            <span
                              style={{
                                fontSize: "0.75rem",
                                color: "#059669",
                                fontWeight: 500,
                              }}
                            >
                              New
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "32px 16px" }}>
            <p style={{ color: "#6b7280", fontWeight: 500, marginBottom: 4 }}>
              No products found
            </p>
            <p style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
              Search for CIN7 products by SKU
            </p>
          </div>
        )}
      </div>

      {results.length > 0 && (
        <div
          className="card-footer"
          style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
        >
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={onClose}
            style={{ borderRadius: 8, fontWeight: 500, padding: "6px 20px" }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleImport}
            disabled={selectedCount === 0 || importing}
            style={{
              borderRadius: 8,
              fontWeight: 600,
              padding: "6px 20px",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {importing ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  style={{ width: 14, height: 14 }}
                />{" "}
                Importing...
              </>
            ) : (
              <>
                Import Selected{selectedCount > 0 ? ` (${selectedCount})` : ""}
              </>
            )}
          </button>
        </div>
      )}
    </Modal>
  );
};

export default Cin7ImportModal;
